import { router } from '@inertiajs/vue3'
import { useConfirm } from 'primevue/useconfirm'
import { onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * Fired on `window` when the guard holds a navigation back, so a control that
 * already moved its own highlight (the admin tab bar) can put it back.
 */
export const NAVIGATION_KEPT_EVENT = 'redata:navigation-kept'

/**
 * RED-361 / RED-384: ask before leaving a form with unsaved changes.
 *
 * Catches two ways out: an Inertia navigation (a tab, a sub-nav link, the
 * navbar) gets the in-app dialog `Jää lehele` / `Lahku salvestamata`; a reload
 * or a closed tab gets the browser's own prompt (browsers do not allow a
 * custom text there). Saves, restores, partial reloads and prefetches are not
 * navigations and pass through.
 *
 * Shared by every part of `Admin → Aruande kontroll` that edits something.
 *
 * @param {() => boolean} isDirty
 */
export function useUnsavedGuard(isDirty) {
  const { t } = useI18n()
  const confirm = useConfirm()
  let leaving = false

  const isNavigation = visit =>
    visit.method === 'get' && !visit.prefetch && !(visit.only?.length > 0) && !(visit.except?.length > 0)

  const removeBeforeListener = router.on('before', event => {
    const visit = event.detail.visit

    if (leaving || !isDirty() || !isNavigation(visit)) {
      return
    }

    confirm.require({
      header: t('messages.admin.report_qc.leave.header'),
      message: t('messages.admin.report_qc.leave.message'),
      icon: 'pi pi-exclamation-triangle',
      rejectProps: { label: t('messages.admin.report_qc.leave.stay'), severity: 'secondary', outlined: true },
      acceptProps: { label: t('messages.admin.report_qc.leave.leave'), severity: 'danger' },
      accept: () => {
        leaving = true
        router.visit(visit.url.href ?? String(visit.url), {
          preserveScroll: visit.preserveScroll,
          preserveState: visit.preserveState,
          replace: visit.replace,
          onFinish: () => {
            leaving = false
          },
        })
      },
    })

    window.dispatchEvent(new CustomEvent(NAVIGATION_KEPT_EVENT))

    return false
  })

  const onBeforeUnload = event => {
    if (!leaving && isDirty()) {
      event.preventDefault()
      event.returnValue = ''
    }
  }

  window.addEventListener('beforeunload', onBeforeUnload)

  onBeforeUnmount(() => {
    removeBeforeListener()
    window.removeEventListener('beforeunload', onBeforeUnload)
  })
}
